/**
 * Catalogo de permissoes do iFretes.
 *
 * Formato: `recurso:acao`. O sufixo `:own` LE-SE como "apenas registros do
 * proprio usuario ou da propria organizacao", mas quem determina o escopo e a
 * flag `scoped`, nao o nome: ha permissoes escopadas com outro nome
 * (`org:member:manage`, `bid:read:freight`). O escopo e resolvido na camada 2
 * do enforcement (SPEC.md 5.4) e nunca apenas no frontend.
 *
 * Este arquivo e a FONTE UNICA: o seed do banco, o guard da API e o render
 * condicional da UI leem daqui. Nenhuma chave de permissao e redigitada.
 */
export const PERMISSION_GROUPS = {
    USERS: 'Usuários',
    ROLES: 'Papéis',
    DRIVERS: 'Motoristas',
    VEHICLES: 'Veículos',
    ORGS: 'Organizações',
    FREIGHTS: 'Fretes',
    BIDS: 'Candidaturas',
    EXECUTION: 'Execução',
    RATINGS: 'Avaliações',
    PLATFORM: 'Plataforma',
};
export const PERMISSIONS = [
    // ---------- Usuarios ----------
    { key: 'user:read', group: PERMISSION_GROUPS.USERS, label: 'Ver usuários' },
    { key: 'user:create', group: PERMISSION_GROUPS.USERS, label: 'Criar usuários' },
    { key: 'user:update', group: PERMISSION_GROUPS.USERS, label: 'Editar usuários' },
    { key: 'user:suspend', group: PERMISSION_GROUPS.USERS, label: 'Suspender usuários' },
    { key: 'user:delete', group: PERMISSION_GROUPS.USERS, label: 'Excluir usuários' },
    { key: 'user:impersonate', group: PERMISSION_GROUPS.USERS, label: 'Assumir identidade de usuário' },
    // ---------- Papeis ----------
    { key: 'role:read', group: PERMISSION_GROUPS.ROLES, label: 'Ver papéis' },
    { key: 'role:create', group: PERMISSION_GROUPS.ROLES, label: 'Criar papéis' },
    { key: 'role:update', group: PERMISSION_GROUPS.ROLES, label: 'Editar papéis' },
    { key: 'role:delete', group: PERMISSION_GROUPS.ROLES, label: 'Excluir papéis' },
    { key: 'role:assign', group: PERMISSION_GROUPS.ROLES, label: 'Atribuir papéis a usuários' },
    // ---------- Motoristas ----------
    { key: 'driver:read', group: PERMISSION_GROUPS.DRIVERS, label: 'Ver todos os motoristas' },
    { key: 'driver:read:own', group: PERMISSION_GROUPS.DRIVERS, label: 'Ver próprio cadastro', scoped: true },
    { key: 'driver:update:own', group: PERMISSION_GROUPS.DRIVERS, label: 'Editar próprio cadastro', scoped: true },
    { key: 'driver:verify', group: PERMISSION_GROUPS.DRIVERS, label: 'Aprovar motorista' },
    { key: 'driver:reject', group: PERMISSION_GROUPS.DRIVERS, label: 'Rejeitar motorista' },
    // ---------- Veiculos ----------
    { key: 'vehicle:read', group: PERMISSION_GROUPS.VEHICLES, label: 'Ver todos os veículos' },
    { key: 'vehicle:crud:own', group: PERMISSION_GROUPS.VEHICLES, label: 'Gerenciar próprios veículos', scoped: true },
    { key: 'vehicle:verify', group: PERMISSION_GROUPS.VEHICLES, label: 'Verificar veículo' },
    // ---------- Organizacoes ----------
    { key: 'org:read', group: PERMISSION_GROUPS.ORGS, label: 'Ver todas as organizações' },
    { key: 'org:crud:own', group: PERMISSION_GROUPS.ORGS, label: 'Gerenciar própria organização', scoped: true },
    { key: 'org:verify', group: PERMISSION_GROUPS.ORGS, label: 'Verificar organização' },
    { key: 'org:member:manage', group: PERMISSION_GROUPS.ORGS, label: 'Gerenciar membros', scoped: true },
    // ---------- Fretes ----------
    { key: 'freight:read:public', group: PERMISSION_GROUPS.FREIGHTS, label: 'Ver vitrine de fretes' },
    { key: 'freight:read:own', group: PERMISSION_GROUPS.FREIGHTS, label: 'Ver próprios fretes', scoped: true },
    { key: 'freight:read:all', group: PERMISSION_GROUPS.FREIGHTS, label: 'Ver todos os fretes' },
    { key: 'freight:create', group: PERMISSION_GROUPS.FREIGHTS, label: 'Criar frete' },
    { key: 'freight:update:own', group: PERMISSION_GROUPS.FREIGHTS, label: 'Editar próprio frete', scoped: true },
    { key: 'freight:publish', group: PERMISSION_GROUPS.FREIGHTS, label: 'Publicar frete' },
    { key: 'freight:cancel:own', group: PERMISSION_GROUPS.FREIGHTS, label: 'Cancelar próprio frete', scoped: true },
    { key: 'freight:cancel:any', group: PERMISSION_GROUPS.FREIGHTS, label: 'Cancelar qualquer frete' },
    // ---------- Candidaturas ----------
    { key: 'bid:read:own', group: PERMISSION_GROUPS.BIDS, label: 'Ver próprias candidaturas', scoped: true },
    { key: 'bid:read:freight', group: PERMISSION_GROUPS.BIDS, label: 'Ver candidaturas do próprio frete', scoped: true },
    { key: 'bid:create', group: PERMISSION_GROUPS.BIDS, label: 'Candidatar-se a frete' },
    { key: 'bid:withdraw:own', group: PERMISSION_GROUPS.BIDS, label: 'Retirar própria candidatura', scoped: true },
    { key: 'bid:accept', group: PERMISSION_GROUPS.BIDS, label: 'Aceitar candidatura' },
    { key: 'bid:reject', group: PERMISSION_GROUPS.BIDS, label: 'Rejeitar candidatura' },
    // ---------- Execucao ----------
    { key: 'assignment:read:own', group: PERMISSION_GROUPS.EXECUTION, label: 'Ver próprias alocações', scoped: true },
    { key: 'assignment:read:all', group: PERMISSION_GROUPS.EXECUTION, label: 'Ver todas as alocações' },
    { key: 'checkpoint:create', group: PERMISSION_GROUPS.EXECUTION, label: 'Registrar checkpoint' },
    { key: 'assignment:complete', group: PERMISSION_GROUPS.EXECUTION, label: 'Confirmar entrega' },
    // ---------- Avaliacoes ----------
    { key: 'rating:create', group: PERMISSION_GROUPS.RATINGS, label: 'Avaliar' },
    { key: 'rating:read', group: PERMISSION_GROUPS.RATINGS, label: 'Ver avaliações' },
    { key: 'rating:moderate', group: PERMISSION_GROUPS.RATINGS, label: 'Moderar avaliações' },
    // ---------- Plataforma ----------
    { key: 'admin:settings', group: PERMISSION_GROUPS.PLATFORM, label: 'Configurar plataforma' },
    { key: 'admin:audit:read', group: PERMISSION_GROUPS.PLATFORM, label: 'Ver trilha de auditoria' },
    { key: 'admin:metrics', group: PERMISSION_GROUPS.PLATFORM, label: 'Ver métricas' },
    { key: 'admin:feature-flags', group: PERMISSION_GROUPS.PLATFORM, label: 'Gerenciar feature flags' },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
const PERMISSION_BY_KEY = new Map(PERMISSIONS.map((p) => [p.key, p]));
export function getPermission(key) {
    return PERMISSION_BY_KEY.get(key);
}
export function isScoped(key) {
    return PERMISSION_BY_KEY.get(key)?.scoped ?? false;
}
//# sourceMappingURL=permissions.js.map